import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import {
  GetProductCodeByWebIdQuery,
  GetProductCodeByWebIdQueryVariables,
} from '@notino/shared/definitions/types';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import productCodeQuery from '../../../../revieve/productCode.graphql';
import {
  extendHAPageViewTracking,
  trackAddToCard,
  trackAddToWishList,
  trackRemoveFromWishList,
  trackViewItem,
  trackViewItemList,
} from '../../tracking';

import { EHairEventName, IHairTrackingEvent } from './model';

export const useTracking = () => {
  const { getTimeFromInit } = useTrackingContext();
  const {
    product: { brand },
  } = useProductDetailContext();
  const apollo = useApolloClient();

  const loadProductCodes = React.useCallback(
    async (ids: string[]) => {
      const { data } = await apollo.query<
        GetProductCodeByWebIdQuery,
        GetProductCodeByWebIdQueryVariables
      >({
        query: productCodeQuery,
        variables: { ids },
        fetchPolicy: 'cache-first',
      });
      return data?.vpProductByIds ?? [];
    },
    [apollo]
  );

  const dispatchTrackingEvent = React.useCallback(
    async (event: IHairTrackingEvent) => {
      const payload = event.payload.payload;

      switch (event.payload.name) {
        // Must stay synchronous (no await before it): Revieve sends the same
        // payload object to GA4 as soon as onTrackingEvent returns.
        case EHairEventName.PageView:
          extendHAPageViewTracking(payload, brand?.id);
          return;
        case EHairEventName.OpenProductDetails:
        case EHairEventName.OpenProductLink:
          await trackViewItem(payload, loadProductCodes);
          return;
        case EHairEventName.ViewItemList:
          // The recommended products live in `items`; the list event itself
          // has no top-level item_id.
          await trackViewItemList(payload.items ?? [], loadProductCodes);
          return;
        case EHairEventName.AddToWishlist:
          await trackAddToWishList(
            payload,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case EHairEventName.RemoveFromWishlist:
          await trackRemoveFromWishList(
            payload,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case EHairEventName.AddToCart:
          await trackAddToCard(payload, loadProductCodes);
          return;
        default:
          break;
      }
    },
    [getTimeFromInit, loadProductCodes, brand]
  );

  return { dispatchTrackingEvent };
};
